import { readMeta } from './page';
import { jwtData } from './jwt';

/** Must not be `_sift` — that id would shadow Sift's `window._sift` command queue. */
export const SIFT_SCRIPT_ELEMENT_ID = 'sift-science-script';

declare global {
  interface Window {
    _sift?: Array<unknown>;
  }
}

function siftQueue(): Array<unknown> {
  if (!Array.isArray(window._sift)) {
    window._sift = [];
  }
  return window._sift;
}

let siftAccountConfigured = false;

export function resetSiftTrackingState(): void {
  siftAccountConfigured = false;
}

function siftIdentity(): { ssoUuid: string | null; sessionId: string | null } {
  const ssoFromMeta = readMeta('account', 'data-account');
  const sessionFromMeta = readMeta('session');

  if (ssoFromMeta) {
    return { ssoUuid: ssoFromMeta, sessionId: sessionFromMeta };
  }

  const jwt = jwtData();
  const ssoUuid = typeof jwt.sub === 'string' ? jwt.sub : null;
  const sessionId = typeof jwt.session_id === 'string' ? jwt.session_id : null;

  return { ssoUuid, sessionId };
}

export function shouldTrackSift(): boolean {
  const siftKey = readMeta('sift');
  if (!siftKey) return false;

  const { ssoUuid } = siftIdentity();
  return Boolean(ssoUuid);
}

export function injectSiftScript(): void {
  if (document.querySelector(`#${SIFT_SCRIPT_ELEMENT_ID}`)) return;

  const script = document.createElement('script');
  script.src = 'https://cdn.siftscience.com/s.js';
  script.id = SIFT_SCRIPT_ELEMENT_ID;
  document.body.appendChild(script);
}

/** Queue account, user, and session once. Pageviews are sent from the router guard. */
export function initSiftAccount(): void {
  if (!shouldTrackSift() || siftAccountConfigured) return;

  const siftKey = readMeta('sift');
  const { ssoUuid, sessionId } = siftIdentity();
  if (!siftKey || !ssoUuid) return;

  const sift = siftQueue();

  sift.push(['_setAccount', siftKey]);
  sift.push(['_setUserId', ssoUuid]);
  sift.push(['_setSessionId', sessionId]);
  siftAccountConfigured = true;
}

export function trackSiftPageview(): void {
  if (!shouldTrackSift()) return;

  initSiftAccount();
  siftQueue().push(['_trackPageview']);
}

/** Start Sift after client-side login when server-rendered meta tags are stale. */
export function startSiftTracking(): void {
  if (!shouldTrackSift() || siftAccountConfigured) return;

  injectSiftScript();
  initSiftAccount();
  trackSiftPageview();
}
